/***
 *
 * @file components/Overlay/index.tsx
 * @description Overlay component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Centres
 * @since 13 Feb 2024
 *
 */

import React, { ReactNode } from "react";
// import "./Overlay.scss";
import styled from "styled-components";
import { StyledOverlay } from "@/shared/StyledComponents";
import classNames from "classnames";

/** defines type of props to be passed in Overlay component */
type T_Props = {
  /** id for overlay component */
  id?: string | undefined,

  /** children components to be rendered in the overlay container */
  children?: ReactNode,

  /** content type of overlay content */
  typeid?: string,

  /** background color CSS property for overlay */
  backgroundColor?: string,

  /** opacity CSS property for overlay */
  overlayOpacity?: number,

  opacity?: number,

  /** z-index CSS property for overlay */
  zIndex?: number,

  /** CSS class to be added to the Overlay component */
  className?: string,

  /** custom CSS styles to be passed in the style html attribute inside Overlay */
  customStyles?: any,
}

/**
 * Name: Overlay
 * 
 * Description: a function component to render an overlay component with data received from Contentful
 * 
 * @param param0 object containing children and other props
 * @example T_Props
 * ```
 *  type T_Props = {
 *    children?: ReactNode,        // optional
 *    typeid?: string,             // optional
 *    backgroundColor?: string,    // optional
 *    overlayOpacity?: number,     // optional
 *    zIndex?: number,             // optional
 *    className?: string,          // optional
 *    customStyles?: any,          // optional
 * }
 * ```
 * @returns an html div container containing the child components to be displayed as an overlay
 */
const Overlay = ({ id = "", children, backgroundColor = "transparent", overlayOpacity = 0, typeid = "", ...props }: T_Props) => {
  return (
    <StyledOverlay id={id} className={classNames(`bp-overlay`, props?.className)} style={{ backgroundColor: `${backgroundColor}${Math.ceil((overlayOpacity || props?.opacity || 0) * 2.55)?.toString(16)}`, zIndex: `${props?.zIndex || 999}`, ...props?.customStyles }} data-testid="Overlay">
      {children}
    </StyledOverlay>
  );
};

export default Overlay;
